import React, { useEffect, useMemo, useState } from 'react';
import { isSettingsDirty, setSettingsDirty } from "../../../util/settingsDirty";
import { useConfirm } from "../../../context/confirm/ConfirmContext";
import { useTranslation } from 'react-i18next';
import './SettingsPageComponent.css';
import BusinessComponent from "./business/BusinessComponent";
import RolesPermissionComponent from "./roles-permission/RolesPermissionComponent";
import AdminComponent from "./admin/AdminComponent";
import { usePermissions } from "../../../context/PermissionsContext";
import { usePlan } from "../../../context/PlanContext";
import PlanComponent from "./plan/PlanComponent";
import PlanCrown from "../../plan/PlanCrown";

const SettingsPageComponent: React.FC = () => {
    const { t } = useTranslation();
    const tr = t; // alias: the tabs map shadows `t` with the tab item
    const confirmModal = useConfirm();
    // Settings is now scoped to the currently-selected Business — Profile
    // lives behind the user icon in the header.
    const { canView, isAdmin } = usePermissions();
    const { hasFeature, openUpsell, consumeSettingsTabRequest, canManagePlan } = usePlan();
    // Per-business roles are a PROFISSIONAL-plan feature: the tab stays visible
    // with a crown and opens the upsell screen instead of the matrix.
    const rolesLocked = !hasFeature('ROLES');

    // Tabs the caller may see. Business = company details OR the business
    // calendar; Roles & Permission = ROLES_PERMISSIONS; Admin = business admin.
    const tabs = useMemo(
        () => [
            {
                key: 'Business',
                label: t('settings.tabs.business'),
                show: canView('BUSINESS_SETTINGS') || canView('WORKING_HOURS') || canView('PAYMENT_SETTINGS'),
                render: () => <BusinessComponent />,
            },
            {
                key: 'RolesPermission',
                label: t('settings.tabs.rolesPermission'),
                show: canView('ROLES_PERMISSIONS'),
                locked: rolesLocked,
                render: () => <RolesPermissionComponent />,
            },
            {
                // Billing: admins only. The backend strips the billing fields and
                // refuses plan changes for everyone else, so this is not the only gate.
                key: 'Plan',
                label: t('settings.tabs.plan'),
                show: isAdmin && canManagePlan,
                render: () => <PlanComponent />,
            },
            {
                key: 'Admin',
                label: t('settings.tabs.admin'),
                show: isAdmin,
                render: () => <AdminComponent />,
            },
        ].filter((t) => t.show),
        [canView, isAdmin, canManagePlan, rolesLocked, t]
    );

    // Opened from a crown / "Ver planos": land on the Plano tab.
    useEffect(() => {
        const requested = consumeSettingsTabRequest();
        if (requested && tabs.some((tab) => tab.key === requested)) setSelectedTab(requested);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consumeSettingsTabRequest, tabs]);

    const [selectedTab, setSelectedTab] = useState<string>('Business');

    // Keep the selected tab on something the caller can actually see.
    useEffect(() => {
        if (tabs.length === 0) return;
        if (!tabs.some((t) => t.key === selectedTab)) {
            setSelectedTab(tabs[0].key);
        }
    }, [tabs, selectedTab]);

    const active = tabs.find((t) => t.key === selectedTab) ?? tabs[0];

    if (tabs.length === 0) {
        return <p className="text-muted fst-italic p-3">{t('settings.noAccess')}</p>;
    }

    return (
        <div data-cy="settings-page">
            <div className="tab-menu">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        data-cy={`settings-tab-${t.key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        className={`tab-button ${selectedTab === t.key ? 'active' : ''}`}
                        onClick={async () => {
                            // Locked by plan → upsell screen, never the tab itself.
                            if ((t as { locked?: boolean }).locked) { openUpsell('ROLES'); return; }
                            // QA decision 2026-07-04: warn before leaving with unsaved
                            // edits (ConfirmModal do app, nunca window.confirm).
                            if (t.key !== selectedTab && isSettingsDirty()) {
                                const ok = await confirmModal({
                                    title: tr('common.confirmTitle'),
                                    message: tr('settings.unsavedTabConfirm'),
                                    danger: true,
                                    confirmLabel: tr('common.discard'),
                                });
                                if (!ok) return;
                            }
                            setSettingsDirty(false);
                            setSelectedTab(t.key);
                        }}
                    >
                        {t.label}
                        {(t as { locked?: boolean }).locked && <PlanCrown />}
                    </button>
                ))}
            </div>

            <div>{active ? active.render() : null}</div>
        </div>
    );
};

export default SettingsPageComponent;
